import React, { useEffect, useState } from 'react';
import { 
  MapPin, Clock, User, ExternalLink, 
  Image as ImageIcon, AlertCircle, CheckCircle, X, Maximize2 
} from 'lucide-react';

const IdentityReport = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null); // For the Photo Modal

  const timeSlots = [
    { label: "11:00 AM - 11:15 AM", hour: 11 },
    { label: "12:00 PM - 12:15 PM", hour: 12 },
    { label: "01:00 PM - 01:15 PM", hour: 13 },
    { label: "02:00 PM - 02:15 PM", hour: 14 },
    { label: "03:00 PM - 03:15 PM", hour: 15 },
    { label: "04:00 PM - 04:15 PM", hour: 16 },
    { label: "05:00 PM - 05:15 PM", hour: 17 },
  ];

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await fetch("https://api-dg3-core-inv5.adminpaymentdailygo.co.in/api/identity/logs");
      const data = await response.json();
      setLogs(data);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const openInMaps = (lat, lng) => {
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, '_blank');
  };

  return (
    <div className="p-4 md:p-8 bg-[#0b0e14] min-h-screen font-sans text-gray-300">
      <div className="max-w-6xl mx-auto">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Identity Verification Logs
            </h1>
            <p className="text-gray-500 text-sm mt-1">Manage and track hourly staff compliance.</p>
          </div>
          <button 
            onClick={fetchLogs} 
            className="bg-[#3b33d5] text-white px-6 py-2.5 rounded-lg text-sm font-bold shadow-lg hover:bg-[#4a41e6] transition-all flex items-center gap-2"
          >
            <Clock size={16} /> Refresh Logs
          </button>
        </div>

        {/* Info Banner - Matching your screenshot style */}
        <div className="bg-[#1c1611] border border-[#332314] rounded-xl p-4 mb-8 flex items-center gap-4">
          <div className="p-2 bg-[#2d1e12] rounded-lg">
            <AlertCircle className="text-[#e89133]" size={20} />
          </div>
          <div>
            <p className="text-[#e89133] font-bold text-sm">Auto-Deletion Warning</p>
            <p className="text-[#8a633a] text-xs">Verification data is automatically cleared every 26 hours for security.</p>
          </div>
        </div>

        {/* Table Container */}
        <div className="bg-[#12151c] rounded-2xl border border-[#1e232d] overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#161b25] text-gray-400 border-b border-[#1e232d]">
                  <th className="p-5 text-[11px] font-bold uppercase tracking-wider">Time Window</th>
                  <th className="p-5 text-[11px] font-bold uppercase tracking-wider">Generated By</th>
                  <th className="p-5 text-[11px] font-bold uppercase tracking-wider">Identity ID</th>
                  <th className="p-5 text-[11px] font-bold uppercase tracking-wider">Geo-Location</th>
                  <th className="p-5 text-[11px] font-bold uppercase tracking-wider text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e232d]">
                {timeSlots.map((slot, index) => {
                  const logEntry = logs.find(log => new Date(log.createdAt).getHours() === slot.hour);

                  return (
                    <tr key={index} className="hover:bg-[#161b25]/50 transition-colors">
                      <td className="p-5">
                        <div className="flex items-center gap-3 text-white font-medium">
                          <Clock size={16} className="text-[#3b33d5]" />
                          {slot.label}
                        </div>
                      </td>
                      
                      <td className="p-5">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-white shadow-inner ${logEntry ? 'bg-[#3b33d5]' : 'bg-gray-800 text-gray-600'}`}>
                            {logEntry ? logEntry.userName.charAt(0) : '?'}
                          </div>
                          <div>
                            <p className="text-white font-semibold text-sm">{logEntry ? logEntry.userName : 'Unknown'}</p>
                            <p className="text-gray-500 text-[11px]">{logEntry ? 'Verified' : 'Log Missing'}</p>
                          </div>
                        </div>
                      </td>

                      <td className="p-5">
                        {logEntry ? (
                          <div className="relative group w-12 h-12 rounded-lg overflow-hidden border border-[#2d3544] cursor-pointer">
                            <img 
                              src={logEntry.image} 
                              alt="Staff" 
                              className="w-full h-full object-cover transition-transform group-hover:scale-110"
                            />
                            <div 
                              onClick={() => setSelectedImage(logEntry.image)}
                              className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Maximize2 size={16} className="text-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-12 h-12 bg-[#161b25] rounded-lg flex items-center justify-center border border-dashed border-[#2d3544]">
                            <ImageIcon size={18} className="text-gray-700" />
                          </div>
                        )}
                      </td>

                      <td className="p-5">
                        {logEntry ? (
                          <button 
                            onClick={() => openInMaps(logEntry.location.lat, logEntry.location.lng)}
                            className="flex items-center gap-2 text-xs font-bold text-gray-400 bg-[#161b25] border border-[#2d3544] px-4 py-2 rounded-lg hover:border-[#3b33d5] hover:text-white transition-all"
                          >
                            <MapPin size={14} /> Open Maps
                          </button>
                        ) : (
                          <span className="text-gray-700 text-xs">—</span>
                        )}
                      </td>

                      <td className="p-5 text-right">
                        {logEntry ? (
                          <div className="inline-flex items-center gap-1.5 text-[#10b981] bg-[#10b981]/10 px-3 py-1.5 rounded-lg text-[11px] font-bold">
                            <CheckCircle size={14} /> COMPLIANT
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 text-red-500 bg-red-500/10 px-3 py-1.5 rounded-lg text-[11px] font-bold">
                            <AlertCircle size={14} /> ABSENT
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* --- PHOTO VIEW MODAL --- */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="relative max-w-4xl w-full bg-[#12151c] rounded-2xl border border-[#1e232d] overflow-hidden">
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-black transition-colors z-10"
            >
              <X size={20} />
            </button>
            <div className="p-2">
              <img 
                src={selectedImage} 
                alt="Full Identity Preview" 
                className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
              />
            </div>
            <div className="p-4 text-center border-t border-[#1e232d]">
              <p className="text-gray-400 text-sm font-medium">High Resolution Identity Proof</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdentityReport;